import React, { useCallback } from "react";
import { Scheduler } from "@/shared";
import { VIEW_TYPES } from "@/shared/Scheduler/constants";
import { Event } from "@/shared/Scheduler/types/events";
import { ViewType } from "@/shared/Scheduler/types/common";
import { Resource } from "@/shared/Scheduler/types/resources";
import { TimelineScrollTarget } from "@/shared/Scheduler/types/views";
import { useDispatchSchedulerTheme } from "./useDispatchSchedulerTheme";

interface DispatchSchedulerPanelProps {
  scheduledEvents: Event[];
  resources: Resource[];
  getEventResourceIds: (event: Event) => (string | number)[];
  selectedDate: Date;
  setSelectedDate: (date: Date) => void;
  onEventChange: (event: Event) => void | Promise<any>;
  onEventDrop: (
    eventId: string,
    newStartTime: Date,
    newResource?: Resource
  ) => void | Promise<any>;
  onEventClick: (event: Event) => void;
  onResourceClick: (resource: Resource) => void;
  onTimeSlotClick: (slotInfo: {
    start: Date;
    end: Date;
    resourceId?: string;
  }) => void;
  onViewChange: (view: ViewType) => void;
  activeView: ViewType;
  selectedDateRange: { start: Date; end: Date };
  onDateRangeChange: (range: { start: Date; end: Date }) => void;
  useDateRange: boolean;
  showMapButton: boolean;
  onMapToggle: () => void;
  isMapVisible: boolean;
  showSchedulerToggle: boolean;
  onSchedulerToggle: () => void;
  isSchedulerVisible: boolean;
  isDraggable?: boolean;
  scrollTarget?: TimelineScrollTarget | null;
  highlightedEventId?: string | null;
}

const DispatchSchedulerPanel: React.FC<DispatchSchedulerPanelProps> = ({
  scheduledEvents,
  resources,
  getEventResourceIds,
  selectedDate,
  setSelectedDate,
  onEventChange,
  onEventDrop,
  onEventClick,
  onResourceClick,
  onTimeSlotClick,
  onViewChange,
  activeView,
  selectedDateRange,
  onDateRangeChange,
  useDateRange,
  showMapButton,
  onMapToggle,
  isMapVisible,
  showSchedulerToggle,
  onSchedulerToggle,
  isSchedulerVisible,
  isDraggable = false,
  scrollTarget = null,
  highlightedEventId = null,
}) => {
  const schedulerTheme = useDispatchSchedulerTheme();
  const isTimelineView = activeView === (VIEW_TYPES.TIMELINE as ViewType);

  const getResourceGroups = useCallback((allResources: Resource[]) => {
    const activeShiftResourceIds = allResources
      .filter((resource: any) => resource.hasActiveShift)
      .map((resource) => resource.id);
    const inactiveShiftResourceIds = allResources
      .filter((resource: any) => !resource.hasActiveShift)
      .map((resource) => resource.id);

    return [
      {
        id: "active-shift",
        label: "Active Shift",
        resourceIds: activeShiftResourceIds,
      },
      {
        id: "inactive-shift",
        label: "Other Resources",
        resourceIds: inactiveShiftResourceIds,
      },
    ].filter((group) => group.resourceIds.length > 0);
  }, []);

  return (
    <Scheduler
      events={scheduledEvents}
      resources={resources}
      getEventResourceIds={getEventResourceIds}
      selectedDate={selectedDate}
      setSelectedDate={setSelectedDate}
      onEventChange={onEventChange}
      onEventDrop={onEventDrop}
      onEventClick={(event) => onEventClick(event)}
      onTimeSlotClick={(slot) =>
        onTimeSlotClick({
          start: slot.start,
          end: slot.end,
          resourceId: slot.resources?.[0]?.id,
        })
      }
      onResourceClick={onResourceClick}
      onViewChange={onViewChange}
      activeView={activeView}
      initialView={VIEW_TYPES.TIMELINE as ViewType}
      visibleViewTypes={[
        VIEW_TYPES.TIMELINE as ViewType,
        VIEW_TYPES.DAY as ViewType,
        VIEW_TYPES.WEEK as ViewType,
        VIEW_TYPES.RESOURCE as ViewType,
        VIEW_TYPES.LIST as ViewType,
      ]}
      startHour={isTimelineView ? 6 : 0}
      endHour={isTimelineView ? 20 : 24}
      className="h-full"
      selectedDateRange={selectedDateRange}
      onDateRangeChange={onDateRangeChange}
      useDateRange={useDateRange}
      showMapButton={showMapButton}
      onMapToggle={onMapToggle}
      isMapVisible={isMapVisible}
      showSchedulerToggle={showSchedulerToggle}
      onSchedulerToggle={onSchedulerToggle}
      isSchedulerVisible={isSchedulerVisible}
      isDraggable={isDraggable}
      schedulerTheme={schedulerTheme}
      getResourceGroups={getResourceGroups}
      scrollTarget={scrollTarget}
      highlightedEventId={highlightedEventId}
    />
  );
};

export default DispatchSchedulerPanel;
